import { useState, useEffect, useMemo } from 'react';
import {
  Shield, Download, Search, Lock, CheckCircle2,
  AlertCircle, Monitor, RefreshCw, Sun, Moon,
} from 'lucide-react';

import useAutoLogout from '../hooks/useAutoLogout';
import API_BASE from '../config';

const THEME_KEY = 'martech_theme';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const THEMES = {
  light: {
    pageBg: '#ffffff',
    bg: '#f9fafb',
    card: '#ffffff',
    border: '#e5e7eb',
    rowBorder: '#f0f1f3',
    text: '#111827',
    subtext: '#6b7280',
    faint: '#9ca3af',
    brand: '#5B4EE8',
    brandDark: '#4338ca',
    inputBg: '#ffffff',
    overlay: 'rgba(0,0,0,0.4)',
    hoverRow: '#fafafa',
    success: '#059669', successBg: '#d1fae5', successBorder: '#a7f3d0',
    danger: '#dc2626', dangerBg: '#fee2e2', dangerBorder: '#fecaca',
    violet: '#7c3aed', violetBg: '#ede9fe', violetBorder: '#ddd6fe',
    blue: '#0369a1', blueBg: '#e0f2fe', blueBorder: '#bae6fd',
    amber: '#b45309', amberBg: '#fef3c7', amberBorder: '#fde68a',
    neutralBg: '#f3f4f6', neutralBorder: '#e5e7eb', neutralText: '#374151',
  },
  dark: {
    pageBg: '#0b0b0d',
    bg: '#0f0f0f',
    card: '#141416',
    border: '#2a2a2a',
    rowBorder: '#1f1f1f',
    text: '#f3f4f6',
    subtext: '#9ca3af',
    faint: '#6b7280',
    brand: '#7C72FF',
    brandDark: '#6a60e0',
    inputBg: '#0f0f0f',
    overlay: 'rgba(0,0,0,0.65)',
    hoverRow: 'rgba(255,255,255,0.02)',
    success: '#34d399', successBg: 'rgba(16,185,129,0.1)', successBorder: 'rgba(52,211,153,0.2)',
    danger: '#f87171', dangerBg: 'rgba(239,68,68,0.08)', dangerBorder: 'rgba(239,68,68,0.2)',
    violet: '#a78bfa', violetBg: 'rgba(139,92,246,0.1)', violetBorder: 'rgba(167,139,250,0.2)',
    blue: '#60a5fa', blueBg: 'rgba(59,130,246,0.1)', blueBorder: 'rgba(96,165,250,0.2)',
    amber: '#fbbf24', amberBg: 'rgba(245,158,11,0.1)', amberBorder: 'rgba(251,191,36,0.2)',
    neutralBg: '#1f1f1f', neutralBorder: '#2a2a2a', neutralText: '#9ca3af',
  },
};

function parseUA(ua) {
  if (!ua) return { browser: 'Unknown', device: 'Desktop' };
  const browser =
    /Edg\//.test(ua)     ? 'Edge'    :
    /Chrome\//.test(ua)  ? 'Chrome'  :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua)  ? 'Safari'  : 'Browser';
  const device = /Mobile|Android|iPhone|iPad/i.test(ua) ? 'Mobile' : 'Desktop';
  return { browser, device };
}

/* Single point of timezone conversion: backend now returns RAW UTC.
   Convert to IST exactly once, here, and nowhere else in this file. */
const toIST = (utcString) => {
  if (!utcString) return '—';
  let s = String(utcString).trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

function csvEsc(val) {
  const s = String(val ?? '');
  return (s.includes(',') || s.includes('"') || s.includes('\n'))
    ? '"' + s.replace(/"/g, '""') + '"'
    : s;
}

export default function AuditLog() {
  useAutoLogout();
  const isMobile = useIsMobile();
  const [themeName, setThemeName] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const C = THEMES[themeName];
  useEffect(() => { localStorage.setItem(THEME_KEY, themeName); }, [themeName]);
  const toggleTheme = () => setThemeName((t) => (t === 'light' ? 'dark' : 'light'));

  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('martech_user') || '{}'); }
    catch { return {}; }
  })();
  const role  = storedUser.role
    || localStorage.getItem('role')
    || sessionStorage.getItem('role')
    || '';
  const token = localStorage.getItem('martech_token')
    || localStorage.getItem('token')
    || sessionStorage.getItem('martech_token')
    || sessionStorage.getItem('token')
    || '';

  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState('');
  const [activeTab,    setActiveTab]    = useState('logins');
  const [searchLogin,  setSearchLogin]  = useState('');
  const [searchAction, setSearchAction] = useState('');

  useEffect(() => {
    if (role !== 'admin') { setLoading(false); return; }
    fetch(`${API_BASE}/api/auth/audit-logs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) setData(d);
        else setFetchError(d.message || 'Failed to load audit logs.');
      })
      .catch(() => setFetchError('Unable to connect. Please try again.'))
      .finally(() => setLoading(false));
  }, [role, token]);

  const loginLogs    = useMemo(() => data?.login_logs      || [], [data]);
  const auditLogs    = useMemo(() => data?.audit_logs      || [], [data]);
  const resets       = useMemo(() => data?.password_resets || [], [data]);
  const failedLogins = loginLogs.filter(l => (l.status || '').toLowerCase() === 'failed').length;

  const filteredLogins = useMemo(() => {
    const q = searchLogin.toLowerCase();
    return loginLogs
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 50)
      .filter(l => !q || (l.email || '').toLowerCase().includes(q));
  }, [loginLogs, searchLogin]);

  const filteredActions = useMemo(() => {
    const q = searchAction.toLowerCase();
    return auditLogs
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .filter(l =>
        !q ||
        (l.action || '').toLowerCase().includes(q) ||
        (l.performed_by || '').toLowerCase().includes(q)
      );
  }, [auditLogs, searchAction]);

  const exportCSV = () => {
    const rows = [
      ['Type', 'Email/User', 'Action/Status', 'Role/Target', 'IP Address', 'Details', 'Date (IST)'].join(','),
      ...loginLogs.map(l => [
        csvEsc('login'), csvEsc(l.email), csvEsc(l.status), csvEsc(l.role),
        csvEsc(l.ip_address), csvEsc(l.user_agent), csvEsc(toIST(l.created_at)),
      ].join(',')),
      ...auditLogs.map(l => [
        csvEsc('action'), csvEsc(l.performed_by), csvEsc(l.action),
        csvEsc(l.target_table), csvEsc(''), csvEsc(JSON.stringify(l.details || {})), csvEsc(toIST(l.created_at)),
      ].join(',')),
      ...resets.map(r => [
        csvEsc('reset'), csvEsc(r.email), csvEsc(r.status), csvEsc(r.requested_by),
        csvEsc(''), csvEsc(''), csvEsc(toIST(r.created_at)),
      ].join(',')),
    ];
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- shared bits ----------

  const Pill = ({ bg, color, border, children }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      background: bg, color, border: `1px solid ${border}`,
      padding: '0.15rem 0.55rem', borderRadius: '999px',
      fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase',
      letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );

  const StatusPill = ({ status }) => {
    const ok = (status || '').toLowerCase() === 'success';
    return <Pill bg={ok ? C.successBg : C.dangerBg} color={ok ? C.success : C.danger} border={ok ? C.successBorder : C.dangerBorder}>{ok ? '✓' : '✗'} {status || 'unknown'}</Pill>;
  };

  const RolePill = ({ role: r }) => {
    const rl = (r || '').toLowerCase();
    const map = {
      admin:   { bg: C.violetBg, color: C.violet, border: C.violetBorder },
      staff:   { bg: C.blueBg, color: C.blue, border: C.blueBorder },
      manager: { bg: C.amberBg, color: C.amber, border: C.amberBorder },
    };
    const s = map[rl] || { bg: C.neutralBg, color: C.neutralText, border: C.neutralBorder };
    return <Pill bg={s.bg} color={s.color} border={s.border}>{r || 'user'}</Pill>;
  };

  const ActionPill = ({ action }) => {
    const map = {
      user_login:          { bg: C.blueBg, color: C.blue, border: C.blueBorder },
      totp_setup_complete: { bg: C.violetBg, color: C.violet, border: C.violetBorder },
      data_rights_export:  { bg: C.amberBg, color: C.amber, border: C.amberBorder },
      data_rights_erase:   { bg: C.dangerBg, color: C.danger, border: C.dangerBorder },
    };
    const s = map[action] || { bg: C.neutralBg, color: C.neutralText, border: C.neutralBorder };
    return <Pill bg={s.bg} color={s.color} border={s.border}>{(action || 'unknown').replace(/_/g, ' ')}</Pill>;
  };

  const ResetStatusPill = ({ status }) => {
    const resolved = (status || '').toLowerCase() === 'resolved';
    return <Pill bg={resolved ? C.successBg : C.amberBg} color={resolved ? C.success : C.amber} border={resolved ? C.successBorder : C.amberBorder}>{status || 'pending'}</Pill>;
  };

  const cardStyle = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px' };

  const StatCard = ({ label, value, icon: Icon, color, bg, border, highlight }) => (
    <div style={{ ...cardStyle, padding: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
        {highlight && value > 0 && <Pill bg={C.dangerBg} color={C.danger} border={C.dangerBorder}>Alert</Pill>}
      </div>
      <p style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0, color: highlight && value > 0 ? C.danger : C.text }}>{value ?? '—'}</p>
      <p style={{ fontSize: '0.75rem', color: C.subtext, fontWeight: '500', margin: '0.15rem 0 0' }}>{label}</p>
    </div>
  );

  const searchInputStyle = {
    width: '100%', padding: '0.6rem 0.75rem 0.6rem 2.25rem', borderRadius: '10px',
    border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontSize: '0.85rem',
    boxSizing: 'border-box', outline: 'none',
  };

  const th = { padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: C.subtext, whiteSpace: 'nowrap' };
  const td = { padding: '0.7rem 1rem', fontSize: '0.85rem', whiteSpace: 'nowrap' };

  const EmptyState = ({ icon: Icon, text, subtext }) => (
    <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: C.faint }}>
      <Icon size={36} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
      <p style={{ fontSize: '0.85rem', color: C.subtext, fontWeight: '600', margin: 0 }}>{text}</p>
      {subtext && <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{subtext}</p>}
    </div>
  );

  // ---------- access denied ----------
  if (!loading && role !== 'admin') {
    return (
      <div style={{ background: C.pageBg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', background: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}>
            <Lock size={28} color={C.danger} />
          </div>
          <h2 style={{ color: C.text, fontSize: '1.25rem', fontWeight: '800', marginBottom: '0.5rem' }}>Access denied</h2>
          <p style={{ color: C.subtext, fontSize: '0.9rem' }}>Admin only — you don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  // ---------- mobile card renderers ----------
  const LoginCard = (log) => {
    const isFailed = (log.status || '').toLowerCase() === 'failed';
    const { browser, device } = parseUA(log.user_agent);
    return (
      <div key={log.login_id} style={{ ...cardStyle, padding: '0.9rem', borderLeft: isFailed ? `3px solid ${C.danger}` : cardStyle.border }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: '600', fontSize: '0.9rem', color: C.text, wordBreak: 'break-word' }}>{log.email || '—'}</span>
          <StatusPill status={log.status} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <RolePill role={log.role} />
          <span style={{ fontSize: '0.75rem', color: C.subtext }}>{browser} · {device}</span>
        </div>
        <div style={{ fontSize: '0.75rem', color: C.faint, fontFamily: 'monospace' }}>{log.ip_address || '—'}</div>
        <div style={{ fontSize: '0.75rem', color: C.subtext, marginTop: '0.35rem' }}>{toIST(log.created_at)}</div>
      </div>
    );
  };

  const ActionCard = (log) => {
    const details = log.details || {};
    const detailStr = Object.entries(details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—';
    return (
      <div key={log.audit_id} style={cardStyle}>
        <div style={{ padding: '0.9rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <ActionPill action={log.action} />
            <span style={{ fontSize: '0.75rem', color: C.faint }}>{toIST(log.created_at)}</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: C.text, fontWeight: '600', marginBottom: '0.2rem' }}>{log.performed_by || '—'}</div>
          <div style={{ fontSize: '0.75rem', color: C.subtext }}>{log.target_table || '—'}</div>
          <div style={{ fontSize: '0.75rem', color: C.faint, marginTop: '0.3rem' }} title={detailStr}>{detailStr}</div>
        </div>
      </div>
    );
  };

  const ResetCard = (r) => (
    <div key={r.reset_id} style={{ ...cardStyle, padding: '0.9rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: '600', fontSize: '0.9rem', color: C.text, wordBreak: 'break-word' }}>{r.email || '—'}</span>
        <ResetStatusPill status={r.status} />
      </div>
      <div style={{ fontSize: '0.75rem', color: C.subtext, marginBottom: '0.3rem' }}>Requested by {r.requested_by || '—'}</div>
      <div style={{ fontSize: '0.75rem', color: C.faint }}>Created: {toIST(r.created_at)}</div>
      <div style={{ fontSize: '0.75rem', color: C.faint }}>Resolved: {toIST(r.resolved_at)}</div>
    </div>
  );

  return (
    <div style={{ background: C.pageBg, minHeight: '100%' }}>
      <div style={{ padding: isMobile ? '1rem' : '2rem 2.5rem', maxWidth: '1600px', margin: '0 auto', boxSizing: 'border-box' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.amberBg, border: `1px solid ${C.amberBorder}` }}>
              <Shield size={20} color={C.amber} />
            </div>
            <div>
              <h1 style={{ color: C.text, fontSize: '1.4rem', fontWeight: '800', margin: 0, letterSpacing: '-0.01em' }}>Audit Logs</h1>
              <p style={{ color: C.subtext, fontSize: '0.85rem', margin: '0.15rem 0 0' }}>Security &amp; compliance monitoring</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              onClick={toggleTheme}
              title={themeName === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer' }}
            >
              {themeName === 'light' ? <Moon size={17} color={C.subtext} /> : <Sun size={17} color={C.subtext} />}
            </button>
            {!loading && (
              <button
                onClick={exportCSV}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '600', color: C.text, background: C.card, border: `1px solid ${C.border}`, cursor: 'pointer' }}
              >
                <Download size={15} /> Export CSV
              </button>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.85rem', marginBottom: '1.75rem' }}>
          <StatCard label="Total Events" value={loading ? '—' : auditLogs.length} icon={Monitor} color={C.violet} bg={C.violetBg} />
          <StatCard label="Total Logins" value={loading ? '—' : loginLogs.length} icon={Shield} color={C.blue} bg={C.blueBg} />
          <StatCard label="Failed Logins" value={loading ? '—' : failedLogins} icon={AlertCircle} color={C.danger} bg={C.dangerBg} highlight />
          <StatCard label="Password Resets" value={loading ? '—' : resets.length} icon={RefreshCw} color={C.amber} bg={C.amberBg} />
        </div>

        {fetchError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '1.25rem', fontSize: '0.85rem', color: C.danger, background: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}>
            <AlertCircle size={16} /> {fetchError}
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: 'inline-flex', gap: '0.25rem', padding: '0.25rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}`, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {[
            { id: 'logins', label: 'Login Activity', count: loginLogs.length },
            { id: 'actions', label: 'Platform Actions', count: auditLogs.length },
            { id: 'resets', label: 'Password Resets', count: null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', border: 'none', cursor: 'pointer',
                background: activeTab === tab.id ? C.card : 'transparent',
                color: activeTab === tab.id ? C.text : C.subtext,
                boxShadow: activeTab === tab.id ? `0 1px 2px rgba(0,0,0,0.06)` : 'none',
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: '700', padding: '0.1rem 0.4rem', borderRadius: '6px', background: C.neutralBg, color: C.neutralText }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* LOGIN ACTIVITY */}
        {activeTab === 'logins' && (
          <div>
            <div style={{ position: 'relative', maxWidth: isMobile ? '100%' : '320px', marginBottom: '1rem' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: C.faint }} />
              <input type="text" placeholder="Search by email..." value={searchLogin} onChange={(e) => setSearchLogin(e.target.value)} style={searchInputStyle} />
            </div>

            {loading ? (
              <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: C.subtext }}>Loading...</div>
            ) : filteredLogins.length === 0 ? (
              <div style={cardStyle}><EmptyState icon={Shield} text="No login records found" /></div>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {filteredLogins.map(LoginCard)}
              </div>
            ) : (
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px' }}>
                    <thead style={{ background: C.bg }}>
                      <tr>{['Status', 'Email', 'Role', 'IP Address', 'Browser / Device', 'Date & Time (IST)'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {filteredLogins.map((log, i) => {
                        const isFailed = (log.status || '').toLowerCase() === 'failed';
                        const { browser, device } = parseUA(log.user_agent);
                        return (
                          <tr key={log.login_id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.rowBorder}`, borderLeft: isFailed ? `2px solid ${C.danger}` : 'none' }}>
                            <td style={td}><StatusPill status={log.status} /></td>
                            <td style={{ ...td, color: C.text, fontWeight: '500' }}>{log.email || '—'}</td>
                            <td style={td}><RolePill role={log.role} /></td>
                            <td style={{ ...td, color: C.faint, fontFamily: 'monospace', fontSize: '0.78rem' }}>{log.ip_address || '—'}</td>
                            <td style={td}><span style={{ color: C.subtext, fontSize: '0.78rem' }}>{browser}</span><span style={{ margin: '0 0.4rem', color: C.faint }}>·</span><span style={{ color: C.faint, fontSize: '0.78rem' }}>{device}</span></td>
                            <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{toIST(log.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PLATFORM ACTIONS */}
        {activeTab === 'actions' && (
          <div>
            <div style={{ position: 'relative', maxWidth: isMobile ? '100%' : '320px', marginBottom: '1rem' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: C.faint }} />
              <input type="text" placeholder="Search by action or user..." value={searchAction} onChange={(e) => setSearchAction(e.target.value)} style={searchInputStyle} />
            </div>

            {loading ? (
              <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: C.subtext }}>Loading...</div>
            ) : filteredActions.length === 0 ? (
              <div style={cardStyle}><EmptyState icon={Monitor} text="No platform actions found" /></div>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {filteredActions.map(ActionCard)}
              </div>
            ) : (
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px' }}>
                    <thead style={{ background: C.bg }}>
                      <tr>{['Action', 'Performed By', 'Target Table', 'Target ID', 'Details', 'Date & Time (IST)'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {filteredActions.map((log, i) => {
                        const details = log.details || {};
                        const detailStr = Object.entries(details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—';
                        const targetId = String(log.target_id || '—');
                        return (
                          <tr key={log.audit_id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.rowBorder}` }}>
                            <td style={td}><ActionPill action={log.action} /></td>
                            <td style={{ ...td, color: C.text, fontWeight: '500' }}>{log.performed_by || '—'}</td>
                            <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{log.target_table || '—'}</td>
                            <td style={{ ...td, color: C.faint, fontFamily: 'monospace', fontSize: '0.78rem' }} title={targetId}>{targetId.length > 10 ? targetId.slice(0, 10) + '…' : targetId}</td>
                            <td style={{ ...td, color: C.faint, fontSize: '0.78rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={detailStr}>{detailStr}</td>
                            <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{toIST(log.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PASSWORD RESETS */}
        {activeTab === 'resets' && (
          <div>
            {loading ? (
              <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: C.subtext }}>Loading...</div>
            ) : resets.length === 0 ? (
              <div style={cardStyle}><EmptyState icon={CheckCircle2} text="No password reset requests" subtext="All clear — no pending or resolved requests." /></div>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {resets.map(ResetCard)}
              </div>
            ) : (
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '650px' }}>
                    <thead style={{ background: C.bg }}>
                      <tr>{['Email', 'Requested By', 'Status', 'Created At (IST)', 'Resolved At (IST)'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {resets.map((r, i) => (
                        <tr key={r.reset_id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.rowBorder}` }}>
                          <td style={{ ...td, color: C.text, fontWeight: '500' }}>{r.email || '—'}</td>
                          <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{r.requested_by || '—'}</td>
                          <td style={td}><ResetStatusPill status={r.status} /></td>
                          <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{toIST(r.created_at)}</td>
                          <td style={{ ...td, color: C.subtext, fontSize: '0.78rem' }}>{toIST(r.resolved_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ height: '2rem' }} />
      </div>
    </div>
  );
}